import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "./Modal";
import { useToast } from "./Toast";
import {
  localServers,
  applications,
  activeUsers,
  type LocalServer,
  type LogEvent,
} from "../data";
import { dotColor } from "../pages/admin/SummaryPage";
import { downloadText, toCsv } from "../utils/download";

export type ModalKind =
  | { kind: "stat"; which: "locals" | "apps" | "users" | "delay" }
  | { kind: "node"; node: LocalServer }
  | { kind: "event"; event: LogEvent }
  | { kind: "export" }
  | null;

export function StatModal({ modal, onClose }: { modal: ModalKind; onClose: () => void }) {
  const open = modal?.kind === "stat";
  const which = modal?.kind === "stat" ? modal.which : null;
  const titles: Record<string, { t: string; s: string }> = {
    locals: { t: "Локальные серверы", s: "14 узлов · 12 онлайн · 2 офлайн" },
    apps: { t: "Опубликованные приложения", s: "127 приложений · 6 отозвано" },
    users: { t: "Активные пользователи", s: "за последние 24 часа · 1 284" },
    delay: { t: "Задержка синхронизации", s: "среднее по узлам · целевое ≤ 10 мин" },
  };
  const tt = which ? titles[which] : { t: "", s: "" };
  return (
    <Modal open={open} onClose={onClose} title={tt.t} subtitle={tt.s} size="lg">
      {which === "locals" && <LocalsTable />}
      {which === "apps" && <AppsTable />}
      {which === "users" && <UsersTable />}
      {which === "delay" && <DelayChart />}
    </Modal>
  );
}

function LocalsTable() {
  return (
    <div className="-mx-1 overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="w-full min-w-[640px] text-[12.5px]">
        <thead>
          <tr className="bg-[var(--color-surface-2)] text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <Th>ID</Th><Th>Локация</Th><Th>Клиенты</Th><Th>Кэш</Th><Th>Посл. синк</Th><Th>Сертификат</Th>
          </tr>
        </thead>
        <tbody>
          {localServers.map((s) => (
            <tr key={s.id} className="border-t border-[var(--color-border)]">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px]">{s.id}</td>
              <td className="whitespace-nowrap px-3 py-2">
                <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: s.status === "online" ? "var(--color-success)" : s.status === "stale" ? "var(--color-warning)" : "var(--color-danger)" }} />
                {s.location}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono">{s.clients}</td>
              <td className="whitespace-nowrap px-3 py-2"><CacheBar gb={s.cacheGB} cap={s.cacheCapGB} /></td>
              <td className="whitespace-nowrap px-3 py-2 font-mono">
                {s.status === "offline" ? <span className="text-[var(--color-danger)]">{s.lastSyncMin} мин</span>
                  : s.lastSyncMin > 30 ? <span className="text-[var(--color-warning)]">{s.lastSyncMin} мин</span>
                  : <span>{s.lastSyncMin} мин</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[var(--color-fg-muted)]">{s.certExpires}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CacheBar({ gb, cap }: { gb: number; cap: number }) {
  const pct = Math.min(100, Math.round((gb / cap) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
        <div className="h-full rounded-full bg-[var(--color-brand-2)]" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-[11px] text-[var(--color-fg-muted)]">{gb}/{cap} GB</span>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function AppsTable() {
  return (
    <div className="-mx-1 overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="w-full min-w-[640px] text-[12.5px]">
        <thead>
          <tr className="bg-[var(--color-surface-2)] text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <Th>Приложение</Th><Th>Версия</Th><Th>Видимость</Th><Th>Разработчик</Th><Th>Статус</Th>
          </tr>
        </thead>
        <tbody>
          {applications.map((a) => (
            <tr key={a.pkg} className="border-t border-[var(--color-border)]">
              <td className="whitespace-nowrap px-3 py-2">
                <div className="font-medium text-[var(--color-fg)]">{a.name}</div>
                <div className="font-mono text-[11px] text-[var(--color-fg-muted)]">{a.pkg}</div>
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono">{a.version}</td>
              <td className="whitespace-nowrap px-3 py-2">
                <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[10.5px]">{a.visibility}</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[var(--color-fg-muted)]">{a.developer}</td>
              <td className="whitespace-nowrap px-3 py-2"><StatusChip status={a.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusChip({ status }: { status: "published" | "revoked" | "draft" }) {
  const map = {
    published: { bg: "var(--color-success-bg)", fg: "var(--color-success)", text: "Опубликовано" },
    revoked: { bg: "var(--color-danger-bg)", fg: "var(--color-danger)", text: "Отозвано" },
    draft: { bg: "var(--color-warning-bg)", fg: "var(--color-warning)", text: "Черновик" },
  } as const;
  const m = map[status];
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium" style={{ background: m.bg, color: m.fg }}>
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: m.fg }} />
      {m.text}
    </span>
  );
}

function UsersTable() {
  return (
    <div className="-mx-1 overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="w-full min-w-[720px] text-[12.5px]">
        <thead>
          <tr className="bg-[var(--color-surface-2)] text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <Th>Пользователь</Th><Th>Логин</Th><Th>Роль</Th><Th>Группа</Th><Th>Устройство</Th><Th>Был онлайн</Th>
          </tr>
        </thead>
        <tbody>
          {activeUsers.map((u) => (
            <tr key={u.login} className="border-t border-[var(--color-border)]">
              <td className="whitespace-nowrap px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--color-surface-3)] text-[10.5px] font-medium text-[var(--color-fg)]">{u.initials}</span>
                  {u.fullName}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[var(--color-fg-muted)]">{u.login}</td>
              <td className="whitespace-nowrap px-3 py-2"><span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[11px]">{u.role}</span></td>
              <td className="whitespace-nowrap px-3 py-2 font-mono">{u.group}</td>
              <td className="whitespace-nowrap px-3 py-2 text-[var(--color-fg-muted)]">{u.device}</td>
              <td className="whitespace-nowrap px-3 py-2 text-[var(--color-fg-muted)]">{u.online}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DelayChart() {
  const data = localServers.filter((s) => s.status !== "offline").map((s) => ({ id: s.id, value: s.lastSyncMin }));
  const max = Math.max(...data.map((d) => d.value), 10);
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-[var(--color-fg-muted)]">
        Последняя задержка синхронизации по каждому узлу. Целевой порог — 10 минут.
      </div>
      <ul className="mt-3 flex flex-col gap-2">
        {data.map((d) => {
          const pct = Math.min(100, (d.value / max) * 100);
          const over = d.value > 10;
          return (
            <li key={d.id} className="flex items-center gap-3 text-[12px]">
              <span className="w-16 shrink-0 whitespace-nowrap font-mono text-[11.5px]">{d.id}</span>
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                <div className="absolute left-0 top-0 h-full rounded-full" style={{ width: `${pct}%`, background: over ? "var(--color-warning)" : "var(--color-brand-2)" }} />
                <div className="absolute top-0 h-full border-r border-dashed border-[var(--color-fg-faint)]" style={{ left: `${Math.min(100, (10 / max) * 100)}%` }} />
              </div>
              <span className="w-16 shrink-0 whitespace-nowrap text-right font-mono text-[11.5px]">{d.value} мин</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function NodeModal({ modal, onClose }: { modal: ModalKind; onClose: () => void }) {
  const navigate = useNavigate();
  const open = modal?.kind === "node";
  const node = modal?.kind === "node" ? modal.node : null;
  if (!node) return <Modal open={false} onClose={onClose} title="">{null}</Modal>;

  const statusLabel: Record<string, string> = { online: "онлайн", stale: "устарел", offline: "офлайн" };
  const statusColor: Record<string, string> = {
    online: "var(--color-success)", stale: "var(--color-warning)", offline: "var(--color-danger)",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2 font-mono">
          <span>{node.id}</span>
          <span className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                style={{ background: statusColor[node.status] + "22", color: statusColor[node.status] }}>
            {statusLabel[node.status]}
          </span>
        </span>
      }
      subtitle={`Локальный сервер · ${node.location}`}
      footer={
        <>
          <button onClick={onClose} className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]">
            Закрыть
          </button>
          <button
            onClick={() => {
              onClose();
              navigate("/admin/locals");
            }}
            className="rounded-lg bg-[var(--color-brand)] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[var(--color-navy-700)]"
          >
            Перейти к узлу →
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-[12.5px] sm:grid-cols-2">
        <Kv label="Адрес" mono value={node.address} />
        <Kv label="Клиенты" mono value={String(node.clients)} />
        <Kv label="Кэш" value={<CacheBar gb={node.cacheGB} cap={node.cacheCapGB} />} />
        <Kv label="Поколение каталога" mono value={String(node.generation)} />
        <Kv label="Последний синк" value={node.lastSyncMin + " мин назад"} />
        <Kv label="Сертификат" mono value={node.certExpires} />
      </div>
    </Modal>
  );
}

export function Kv({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className={"mt-0.5 text-[var(--color-fg)] " + (mono ? "font-mono" : "")}>{value}</div>
    </div>
  );
}

export function EventModal({ modal, onClose }: { modal: ModalKind; onClose: () => void }) {
  const navigate = useNavigate();
  const open = modal?.kind === "event";
  const ev = modal?.kind === "event" ? modal.event : null;
  if (!ev) return <Modal open={false} onClose={onClose} title="">{null}</Modal>;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: dotColor(ev.color) }} />Событие журнала</span>}
      subtitle={<span className="font-mono">{ev.id} · {ev.time}</span>}
      footer={
        <>
          <button onClick={onClose} className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]">Закрыть</button>
          <button
            onClick={() => {
              onClose();
              navigate(`/admin/log?q=${encodeURIComponent(ev.id)}`);
            }}
            className="rounded-lg bg-[var(--color-brand)] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[var(--color-navy-700)]"
          >Открыть в журнале →</button>
        </>
      }
    >
      <div className="space-y-4 text-[12.5px]">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
          <div className="text-[var(--color-fg)]">
            <span className="font-mono font-medium">{ev.who}</span>{" "}
            <span className="text-[var(--color-fg-muted)]">{ev.text}</span>
          </div>
        </div>
        <p className="text-[var(--color-fg-muted)]">{ev.detail}</p>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 pt-2 sm:grid-cols-2">
          <Kv label="Тип" mono value={ev.type} />
          <Kv label="Источник" mono value={ev.source} />
          <Kv label="Объект" mono value={ev.target} />
          <Kv label="Время" mono value={ev.time} />
        </div>
      </div>
    </Modal>
  );
}

export function ExportModal({ modal, onClose }: { modal: ModalKind; onClose: () => void }) {
  const open = modal?.kind === "export";
  const [format, setFormat] = useState<"csv" | "json" | "pdf">("pdf");
  const push = useToast();
  const submit = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === "pdf") {
      onClose();
      window.setTimeout(() => window.print(), 100);
      return;
    }
    if (format === "json") {
      downloadText(
        `astramarket-summary-${stamp}.json`,
        JSON.stringify({ generated_at: new Date().toISOString(), locals: localServers, applications }, null, 2),
        "application/json;charset=utf-8",
      );
    } else {
      downloadText(
        `astramarket-summary-${stamp}.csv`,
        "\ufeff" + toCsv([
          ["Раздел", "ID", "Название", "Статус", "Значение"],
          ...localServers.map((node) => ["Локал", node.id, node.location, node.status, node.clients]),
          ...applications.map((app) => ["Приложение", app.pkg, app.name, app.status, app.version]),
        ]),
        "text/csv;charset=utf-8",
      );
    }
    push({ kind: "success", text: `Сводка выгружена в ${format.toUpperCase()}` });
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Экспорт сводки"
      subtitle="Выберите формат выгрузки"
      size="sm"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]">Отмена</button>
          <button onClick={submit} className="rounded-lg bg-[var(--color-brand)] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[var(--color-navy-700)]">Экспортировать</button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        {(["pdf", "csv", "json"] as const).map((f) => (
          <label key={f} className={"flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-[13px] transition-colors " + (format === f ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5" : "border-[var(--color-border)] hover:bg-[var(--color-surface-2)]")}>
            <input type="radio" name="format" checked={format === f} onChange={() => setFormat(f)} className="h-3.5 w-3.5 accent-[var(--color-brand)]" />
            <div>
              <div className="font-medium uppercase text-[var(--color-fg)]">{f}</div>
              <div className="text-[11.5px] text-[var(--color-fg-muted)]">
                {f === "pdf" && "Печатная сводка с диаграммой меша"}
                {f === "csv" && "Таблицы локалов и приложений"}
                {f === "json" && "Машиночитаемая выгрузка состояния"}
              </div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
}
